import { ColDef, ColGroupDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GetRowIdParams, GridOptions, RowEditingStartedEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import {
  CellClickedEvent,
  CellKeyDownEvent,
  RowEditingStoppedEvent
} from 'ag-grid-community/dist/lib/events';
import styles from './Table.module.css';
type TableProps<TData> = {
  data: TData[];
  columnDef: (ColDef<TData> | ColGroupDef<TData>)[] | null;
  columnTypes?: { [key: string]: ColDef<TData> };
  gridOptions?: GridOptions<TData>;
  loading?: boolean;
  testId?: string;
  cellValueChanged?: (params: CellKeyDownEvent<TData>) => void;
  onRowEditingStopped?: (params: RowEditingStoppedEvent) => void;
  onRowEditingStarted?: (params: RowEditingStartedEvent) => void;
  onCellClicked?: (params: CellClickedEvent) => void;
  onEnterPressed?: (params: RowEditingStoppedEvent) => void;
  onRef?: (ref: AgGridReact) => void;
};

const Table = <T,>({
  data,
  columnDef,
  columnTypes,
  gridOptions,
  loading = false,
  testId = 'table',
  cellValueChanged,
  onRowEditingStarted,
  onRowEditingStopped,
  onEnterPressed,
  onCellClicked,
  onRef
}: TableProps<T>) => {
  const gridRef = useRef<AgGridReact>(null);

  const handleKeyDown = (val: CellKeyDownEvent<T>) => {
    if ((val.event as Event & { key: string })?.key === 'Enter') {
      val.event?.preventDefault();
      val.event?.stopImmediatePropagation();
      onEnterPressed && onEnterPressed(val);
    }
  };

  useEffect(() => {
    if (onRef && gridRef?.current) {
      onRef(gridRef.current);
    }
  }, [onRef]);

  useEffect(() => {
    if (!gridRef.current?.api) return;

    if (loading) {
      gridRef.current.api.showLoadingOverlay();
      return;
    }

    if (data.length === 0) {
      gridRef.current.api.showNoRowsOverlay();
      return;
    }

    gridRef.current.api.hideOverlay();
  }, [data.length, loading]);

  const onFirstDataRendered = useCallback(() => {
    if (gridRef?.current?.api) {
      gridRef?.current?.api?.sizeColumnsToFit();
      gridRef.current.api.setDomLayout('autoHeight');
    }
  }, []);

  function editingStarted(params: RowEditingStartedEvent<T>) {
    if (onRowEditingStarted) {
      return onRowEditingStarted(params);
    }
  }

  function editingStopped(params: RowEditingStoppedEvent<T>) {
    if (onRowEditingStopped) {
      return onRowEditingStopped(params);
    }
  }

  function cellClicked(params: CellClickedEvent<T>) {
    if (onCellClicked) {
      return onCellClicked(params);
    }
  }

  return (
    <div className={'ag-theme-material w-full ' + styles.slim} data-testid={testId}>
      <AgGridReact
        onCellKeyDown={(event) => handleKeyDown(event as CellKeyDownEvent)}
        ref={gridRef}
        rowData={data}
        onFirstDataRendered={onFirstDataRendered}
        columnDefs={columnDef}
        columnTypes={columnTypes || {}}
        gridOptions={gridOptions}
        overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Getting data...</span>'}
        getRowId={(params: GetRowIdParams) => params.data.id}
        onCellValueChanged={cellValueChanged}
        rowModelType="clientSide"
        onRowEditingStopped={editingStopped}
        onRowEditingStarted={editingStarted}
        stopEditingWhenCellsLoseFocus={false}
        onCellClicked={cellClicked}
        editType="fullRow"
      />
    </div>
  );
};

export default Table;
